// useCurrentMenu.js
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApiMenu } from '@/composables/useApiMenu'
import { useMenuStore } from '@/store/menu.store'

/**
 * menuType === 'DIR'인 경우, 하위 메뉴 중 menuLink가 있는 첫 번째 메뉴를 찾음
 */
function findFirstActualMenu(menu, allMenus) {
  if (!menu) return null
  
  // menuLink가 있고 'N' 또는 'dir'이 아니면 현재 메뉴가 실제 페이지
  if (menu.menuLink && menu.menuLink !== 'N' && menu.menuLink !== 'dir') {
    return menu
  }
  
  // menuType이 'DIR'이면 하위 메뉴 찾기
  if (menu.menuType === 'DIR' || menu.menuType === 'dir') {
    const childMenus = allMenus.filter(m => m.upMenuId === menu.menuId)
    for (const child of childMenus) {
      const found = findFirstActualMenu(child, allMenus)
      if (found) return found
    }
  }
  
  return null
}

function resolveMenuById(menuId, menus) {
  if (!menuId || !Array.isArray(menus) || menus.length === 0) return null

  let found = menus.find((menu) => menu.menuId === menuId) || null
  if (!found) return null

  if (found.menuType === 'DIR') {
    const actualMenu = findFirstActualMenu(found, menus)
    if (actualMenu) {
      found = actualMenu
    }
  }

  return found
}

export function useCurrentMenu() {
  const route = useRoute()
  const router = useRouter()
  const { menusWithLevel, getMenuBreadcrumb } = useApiMenu()
  const menuStore = useMenuStore()

  const shouldRun = computed(() => {
    const layout = route.meta?.layout || 'default'
    return layout !== 'blank'
  })

  // 쿼리에서 menuId 가져오기
  const menuIdFromQuery = computed(() => {
    const q = route.query?.menuId
    return Array.isArray(q) ? q[0] : q || null
  })

  const currentMenu = computed(() => {
    if (!shouldRun.value) return null

    const menus = menusWithLevel.value || []
    if (!menus.length) return null

    // 1) query.menuId 최우선
    if (menuIdFromQuery.value) {
      let byQuery = resolveMenuById(menuIdFromQuery.value, menus)
      if (byQuery) {
        // store와 싱크
        if (menuStore.currentMenuId !== byQuery.menuId) {
          menuStore.setCurrentMenu(byQuery.menuId)
        }
        return byQuery
      }
    }

    const path = route.path

    // 2) store.currentMenuId (버튼 클릭 등에서 저장된 값)
    // 같은 서비스 섹션(첫 번째 경로 세그먼트)인 경우에만 허용
    // → /expr/rsvt, /expr/done 등 예약/완료 페이지에서 store의 menuId 유지
    // → /login 등 다른 섹션 메뉴가 stale하게 남아있으면 반환하지 않음
    // 단, 현재 route path에 정확히 매칭되는 메뉴가 존재하면 stale store 값 무시
    if (menuStore.currentMenuId) {
      // 현재 경로에 exact match가 있으면 step 3에서 처리 (stale store 무시)
      const hasExactPathMatch = menus.some(m =>
        (m.path === path || m.menuLink === path) &&
        m.menuType !== 'DIR' &&
        m.menuLink !== 'dir' &&
        m.menuLink !== 'N'
      )

      if (!hasExactPathMatch) {
        let byId = menus.find(m => m.menuId === menuStore.currentMenuId)
        if (byId) {
          // DIR 타입이면 실제 페이지 메뉴 찾기
          if (byId.menuType === 'DIR') {
            const actualMenu = findFirstActualMenu(byId, menus)
            if (actualMenu) {
              byId = actualMenu
            }
          }
          const menuPath = byId.path || byId.menuLink
          if (menuPath && menuPath !== 'dir' && menuPath !== 'N') {
            const menuFirstSeg = menuPath.split('/').filter(Boolean)[0]
            const routeFirstSeg = path.split('/').filter(Boolean)[0]
            if (menuFirstSeg && routeFirstSeg && menuFirstSeg === routeFirstSeg) {
              // bbsId가 있는 경우(BoardList 등), stale 메뉴의 bbsId와 현재 bbsId가 다르면 무시
              const routeBbsId = route.params?.bbsId
              if (routeBbsId && !menuPath.includes(routeBbsId)) {
                // bbsId 불일치 → stale store 값 무시, fallback으로 진행
              } else {
                return byId
              }
            }
          }
        }
      }
    }

    // 3) fallback: path 기준
    if (!path) return null

    let exact =
      menus.find(m => m.path === path || m.menuLink === path) || null
    if (exact) return exact

    const routeSegs = path.split('/').filter(Boolean)
    let bestMenu = null
    let bestScore = -1

    menus.forEach(m => {
      const menuPath = m.path || m.menuLink
      if (!menuPath) return

      const menuSegs = menuPath.split('/').filter(Boolean)

      let i = 0
      while (
        i < routeSegs.length &&
        i < menuSegs.length &&
        routeSegs[i] === menuSegs[i]
      ) {
        i++
      }

      let score = i
      const menuLast = menuSegs[menuSegs.length - 1]
      if (menuLast && routeSegs.includes(menuLast)) {
        score += 0.5
      }

      if (score > bestScore) {
        bestScore = score
        bestMenu = m
      }
    })

    return bestMenu
  })

  const currentTitle = computed(
    () => currentMenu.value?.menuNm || ''
  )

  const currentBreadcrumbs = computed(() => {
    if (!currentMenu.value) return []
    return getMenuBreadcrumb(currentMenu.value.menuId)
  })

  // route 바뀔 때도 query.menuId를 우선으로 store에 반영
  watch(
    () => [route.path, route.query.menuId, menusWithLevel.value.length],
    () => {
      const menus = menusWithLevel.value || []
      if (!menus.length) return

      // 1) query.menuId 우선
      if (menuIdFromQuery.value) {
        let byQuery = resolveMenuById(menuIdFromQuery.value, menus)
        if (byQuery) {
          menuStore.setCurrentMenu(byQuery.menuId)
          return
        }
      }

      // 2) query 없으면 path 기준으로 자동 매칭
      const path = route.path
      const exact = menus.find(
        m =>
          (m.path === path || m.menuLink === path) &&
          m.menuType !== 'DIR' &&
          m.path !== 'dir' &&
          m.menuLink !== 'dir' &&
          m.menuLink !== 'N'
      )

      if (exact) {
        menuStore.setCurrentMenu(exact.menuId)
        return
      }

      // 3) bbsId param이 있는 경우(BoardList 등), 해당 bbsId를 포함하는 메뉴로 store 업데이트
      // → 알립니다 방문 후 FAQ로 이동해도 stale menuId가 남지 않도록 보정
      const bbsId = route.params?.bbsId
      if (bbsId) {
        const bbsMenu = menus.find(m =>
          m.menuType !== 'DIR' &&
          m.menuLink !== 'dir' &&
          m.menuLink !== 'N' &&
          (m.path || m.menuLink || '').includes(bbsId)
        )
        if (bbsMenu) {
          menuStore.setCurrentMenu(bbsMenu.menuId)
          return
        }
      }

      const routeSegs = path.split('/').filter(Boolean)
      let bestMenu = null
      let bestScore = -1

      menus.forEach(m => {
        const menuPath = m.path || m.menuLink
        if (!menuPath || menuPath === 'dir' || menuPath === 'N' || m.menuType === 'DIR') return

        const menuSegs = menuPath.split('/').filter(Boolean)

        let i = 0
        while (
          i < routeSegs.length &&
          i < menuSegs.length &&
          routeSegs[i] === menuSegs[i]
        ) {
          i++
        }

        let score = i
        const menuLast = menuSegs[menuSegs.length - 1]
        if (menuLast && routeSegs.includes(menuLast)) {
          score += 0.5
        }

        if (score > bestScore) {
          bestScore = score
          bestMenu = m
        }
      })

      // score 기반 부분 매칭은 store를 덮어쓰지 않음
      // → 예약/완료 페이지 등 메뉴 트리에 없는 페이지에서 이전 menuId가 유지되도록
      // if (bestMenu && bestScore > 0) {
      //   menuStore.setCurrentMenu(bestMenu.menuId)
      // }
    }
  )

  // query.menuId가 있는데 메뉴 목록에 없으면 404로 이동
  watch(
    () => [menuIdFromQuery.value, menusWithLevel.value.length],
    ([menuId, menuCount]) => {
      if (!menuId || !menuCount) return
      const found = menusWithLevel.value.find(m => m.menuId === menuId)
      if (!found) {
        router.replace({ path: '/notFound' })
      }
    },
    { immediate: true }
  )

  return {
    currentMenu,
    currentTitle,
    currentBreadcrumbs
  }
}
